import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/** Fond semi-transparent derrière un panneau latéral droit. */
export const trainerRightPanelBackdropClass = 'absolute inset-0 bg-black/20 backdrop-blur-[2px]';

/** Conteneur plein écran pour backdrop + panneau (justify-end). */
export const trainerRightPanelOverlayClass =
  'fixed inset-0 flex justify-end rtl:justify-start';

const shellBaseClass =
  'flex min-h-0 w-full flex-col overflow-hidden border-s border-[#E5DDD2] bg-[#FDFBF7] shadow-2xl ltr:animate-slide-in-right rtl:animate-slide-in-left';

const shellWidthClass = {
  xs: 'w-80 max-w-[20rem]',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
} as const;

export type TrainerRightPanelSize = keyof typeof shellWidthClass;

/** Panneau dans un overlay flex (relative z-10, h-full). */
export function trainerRightPanelShellClass(size: TrainerRightPanelSize = 'md') {
  return cn('relative z-10 h-full max-h-[100dvh]', shellBaseClass, shellWidthClass[size]);
}

/** Panneau en position fixed (hors overlay flex). */
export function trainerRightPanelShellFixedClass(size: TrainerRightPanelSize = 'md') {
  return cn(
    'fixed end-0 top-0 z-50 h-[100dvh] max-h-[100dvh] animate-in duration-200',
    shellBaseClass,
    shellWidthClass[size],
  );
}

/** Zone scrollable du corps du panneau. */
export const trainerRightPanelBodyClass = 'min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6';

/** Pied de panneau (actions). */
export const trainerRightPanelFooterClass =
  'shrink-0 border-t border-[#E5DDD2] bg-white px-5 py-4 sm:px-6';

/** Champ formulaire dans un panneau formateur / institution. */
export const trainerRightPanelFieldClass = cn(
  'w-full rounded-xl border border-[#E5DDD2] bg-white px-3 py-2 text-sm text-[#001B3D]',
  'placeholder:text-[#5C5C5C]/60',
  'focus:border-[#B38E5D] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,#B38E5D_22%,transparent)]',
);

/** Libellé de champ dans un panneau. */
export const trainerRightPanelLabelClass = 'mb-1.5 block text-sm font-medium text-[#001B3D]';

export type TrainerRightPanelProps = {
  open: boolean;
  onClose: () => void;
  size?: TrainerRightPanelSize;
  zIndex?: number;
  closeOnBackdrop?: boolean;
  portal?: boolean;
  className?: string;
  children: ReactNode;
  'aria-labelledby'?: string;
};

/**
 * Overlay + coque de panneau latéral droit (charte formateur / institution).
 * Le contenu attend généralement un `TrainerRightPanelHeader` en premier enfant.
 */
export function TrainerRightPanel({
  open,
  onClose,
  size = 'md',
  zIndex = 50,
  closeOnBackdrop = true,
  portal = true,
  className,
  children,
  'aria-labelledby': ariaLabelledby,
}: TrainerRightPanelProps) {
  if (!open) return null;

  const node = (
    <div
      className={trainerRightPanelOverlayClass}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledby}
    >
      <div
        className={trainerRightPanelBackdropClass}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden
      />
      <div className={cn(trainerRightPanelShellClass(size), className)}>{children}</div>
    </div>
  );

  if (portal && typeof document !== 'undefined') {
    return createPortal(node, document.body);
  }
  return node;
}
